import React, { memo } from 'react';
import type { ButtonProps } from '../../types';

/**
 * Button - A versatile button component with multiple variants and sizes
 * 
 * @example
 * <Button variant="primary" size="large" icon={<Star />}>התחל משחק</Button>
 * <Button variant="fun" loading>טוען...</Button>
 */

export const Button: React.FC<ButtonProps> = memo(({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon,
  className = '',
}) => {
  const baseClasses = `
    inline-flex items-center justify-center gap-2 sm:gap-3
    font-heebo font-bold
    rounded-full
    transition-all duration-150
    transform active:scale-95
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    focus:outline-none focus:ring-4
    btn-3d
  `;

  const variantClasses: Record<string, string> = {
    primary: `
      bg-blue-400 dark:bg-blue-500 border-blue-600 dark:border-blue-700
      text-white
      hover:bg-blue-300 dark:hover:bg-blue-400
      focus:ring-blue-300 dark:focus:ring-blue-500/50
    `,
    secondary: `
      bg-purple-400 dark:bg-purple-500 border-purple-600 dark:border-purple-700
      text-white
      hover:bg-purple-300 dark:hover:bg-purple-400
      focus:ring-purple-300 dark:focus:ring-purple-500/50
    `,
    success: `
      bg-green-400 dark:bg-green-500 border-green-600 dark:border-green-700
      text-white
      hover:bg-green-300 dark:hover:bg-green-400
      focus:ring-green-300 dark:focus:ring-green-500/50
    `,
    fun: `
      bg-orange-400 dark:bg-orange-500 border-orange-600 dark:border-orange-700
      text-white
      hover:bg-orange-300 dark:hover:bg-orange-400
      focus:ring-orange-300 dark:focus:ring-orange-500/50
    `,
    danger: `
      bg-red-500 dark:bg-red-600 border-red-700 dark:border-red-800
      text-white
      hover:bg-red-400 dark:hover:bg-red-500
      focus:ring-red-300 dark:focus:ring-red-500/50
    `,
    ghost: `
      bg-transparent border-transparent
      text-gray-600 dark:text-gray-300
      hover:bg-gray-100 dark:hover:bg-dark-surface
      focus:ring-gray-300 dark:focus:ring-gray-500/50
      shadow-none hover:shadow-none
    `,
    outline: `
      bg-transparent border-gray-300 dark:border-dark-border
      text-gray-700 dark:text-dark-text
      hover:bg-gray-50 dark:hover:bg-dark-surface
      focus:ring-gray-300 dark:focus:ring-gray-500/50
    `,
  };

  const sizeClasses: Record<string, string> = {
    small: 'px-3 sm:px-4 py-1.5 sm:py-2 text-base sm:text-lg min-h-[40px] sm:min-h-[44px]',
    medium: 'px-4 sm:px-6 py-2 sm:py-3 text-lg sm:text-xl min-h-[48px] sm:min-h-[56px]',
    large: 'px-5 sm:px-8 py-3 sm:py-4 text-xl sm:text-2xl min-h-[56px] sm:min-h-[72px]',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variantClasses[variant] || variantClasses.primary}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {loading ? (
        <span className="animate-spin">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </span>
      ) : icon ? (
        <span>{icon}</span>
      ) : null}
      {children}
    </button>
  );
});

Button.displayName = 'Button';
